import React, { useState } from 'react';
import { Modal, Spin, Space, Tag, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import dayjs from 'dayjs';
import { attendanceAPI } from '../services/api';

// 휴가 유형별 컬러 정의 (대시보드와 동일)
const TYPE_COLORS = {
    annual_leave: '#1890ff',
    sick_leave: '#ff4d4f',
    personal_leave: '#fa8c16',
    bereavement_leave: '#722ed1',
    half_day_morning: '#13c2c2',
    half_day_afternoon: '#2f54eb',
};

// FullCalendar 이벤트 변환 함수 (대시보드와 동일한 로직)
const convertToFCEvents = (rawEvents) => {
    // 1단계: 사용자+유형+상태별로 날짜 수집
    const groups = {};
    rawEvents.forEach(event => {
        const key = `${event.user.id}-${event.attendance_type}-${event.status}`;
        if (!groups[key]) {
            groups[key] = {
                user: event.user,
                attendance_type: event.attendance_type,
                status: event.status,
                reason: event.reason,
                dates: [],
            };
        }
        groups[key].dates.push(event.date);
    });

    const fcEvents = [];

    Object.values(groups).forEach(group => {
        // 날짜 정렬
        group.dates.sort();

        // 2단계: 연속된 날짜끼리만 묶기 (실제 연속된 날짜만)
        const segments = [];
        let currentSegment = [group.dates[0]];

        for (let i = 1; i < group.dates.length; i++) {
            const prev = dayjs(group.dates[i - 1]);
            const curr = dayjs(group.dates[i]);
            const diff = curr.diff(prev, 'day');

            // 실제 연속된 날짜만 묶기 (1일 차이만 연속으로 인정)
            if (diff === 1) {
                currentSegment.push(group.dates[i]);
            } else {
                // 주말을 건너뛴 경우 확인 (금요일 -> 월요일)
                const isPrevFriday = prev.day() === 5; // 금요일
                const isCurrMonday = curr.day() === 1;  // 월요일
                
                if (isPrevFriday && isCurrMonday && diff === 3) {
                    // 금요일 -> 월요일인 경우에만 연속으로 처리
                    currentSegment.push(group.dates[i]);
                } else {
                    // 그 외의 경우는 별도 세그먼트로 분리
                    segments.push(currentSegment);
                    currentSegment = [group.dates[i]];
                }
            }
        }
        segments.push(currentSegment);

        // 3단계: 각 segment를 별도 FC 이벤트로 생성
        segments.forEach(segment => {
            const startDate = segment[0];
            const endDate = dayjs(segment[segment.length - 1])
                .add(1, 'day')
                .format('YYYY-MM-DD');

            const color = group.status === 'approved'
                ? (TYPE_COLORS[group.attendance_type] || '#999')
                : '#bfbfbf';

            fcEvents.push({
                title: `${group.user.name}${group.status === 'pending' ? '*' : ''}`,
                start: startDate,
                end: endDate,
                backgroundColor: color,
                borderColor: color,
                textColor: '#fff',
                extendedProps: {
                    user: group.user,
                    attendance_type: group.attendance_type,
                    status: group.status,
                    reason: group.reason,
                    typeName: getTypeDisplay(group.attendance_type),
                    totalDays: segment.length,
                    startDate,
                    endDate: segment[segment.length - 1],
                },
            });
        });
    });

    return fcEvents;
};

// 연차 타입에 따른 표시 (대시보드와 통일)
const getTypeDisplay = (type) => {
    const typeMap = {
        'annual_leave': '연차',
        'sick_leave': '병가',
        'personal_leave': '건강검진',
        'bereavement_leave': '경조휴가', // 대시보드와 통일: '경조사' → '경조휴가'
        'half_day_morning': '오전반차', // 대시보드와 통일: 띄어쓰기 제거
        'half_day_afternoon': '오후반차', // 대시보드와 통일: 띄어쓰기 제거
        'maternity_leave': '출산휴가',
        'paternity_leave': '육아휴직',
        'health_checkup': '건강검진',
        'special_leave': '특별휴가'
    };
    return typeMap[type] || type;
};

const VacationCalendarModal = ({ visible, onClose }) => {
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [calendarLoading, setCalendarLoading] = useState(false);

    // 캘린더 데이터 로드
    const loadCalendarData = async (dateInfo) => {
        setCalendarLoading(true);
        try {
            const current = dayjs(dateInfo.start).add(15, 'day');
            const params = { year: current.year(), month: current.month() + 1 };
            const response = await attendanceAPI.getTeamCalendar(params);
            const rawEvents = response.data?.data?.events || response.data?.events || [];
            setCalendarEvents(convertToFCEvents(rawEvents));
        } catch (error) {
            message.error('휴가 일정 조회에 실패했습니다');
            console.error('Calendar load error:', error);
        } finally {
            setCalendarLoading(false);
        }
    };

    // 캘린더 모달 열릴 때 초기 데이터 로드
    const handleModalOpen = async () => {
        if (visible) {
            setCalendarLoading(true);
            try {
                const now = dayjs();
                const params = { year: now.year(), month: now.month() + 1 };
                const response = await attendanceAPI.getTeamCalendar(params);
                const rawEvents = response.data?.data?.events || response.data?.events || [];
                setCalendarEvents(convertToFCEvents(rawEvents));
            } catch (error) {
                message.error('휴가 일정 조회에 실패했습니다');
            } finally {
                setCalendarLoading(false);
            }
        }
    };

    React.useEffect(() => {
        handleModalOpen();
    }, [visible]);

    return (
        <Modal
            title="팀 휴가 현황"
            open={visible}
            onCancel={onClose}
            footer={[
                <Space key="footer">
                    <CloseOutlined 
                        onClick={onClose} 
                        style={{ 
                            cursor: 'pointer', 
                            fontSize: 16, 
                            color: '#666',
                            '&:hover': { color: '#1890ff' }
                        }}
                    />
                </Space>
            ]}
            width={900}
            style={{ top: 20 }}
            afterOpenChange={(open) => {
                if (open) {
                    // 모달 완전히 열린 후 FullCalendar 크기 재계산
                    setTimeout(() => {
                        window.dispatchEvent(new Event('resize'));
                    }, 100);
                }
            }}
        >
            <Spin spinning={calendarLoading}>
                {/* 범례 */}
                <div style={{ marginBottom: 12, textAlign: 'center' }}>
                    <Space wrap>
                        {Object.entries({
                            annual_leave: '연차',
                            sick_leave: '병가',
                            personal_leave: '건강검진',
                            bereavement_leave: '경조휴가',
                            half_day_morning: '오전반차',
                            half_day_afternoon: '오후반차'
                        }).map(([key, name]) => (
                            <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                <span style={{
                                    display: 'inline-block',
                                    width: 12, height: 12,
                                    borderRadius: 2,
                                    backgroundColor: TYPE_COLORS[key],
                                }} />
                                <span style={{ fontSize: 12 }}>{name}</span>
                            </span>
                        ))}
                        <span style={{ color: '#bfbfbf', fontSize: 12 }}>│ 회색: 신청중</span>
                    </Space>
                </div>

                {/* FullCalendar CSS */}
                <style>{`
                    .fc .fc-toolbar-title { font-size: 16px; }
                    .fc .fc-button { font-size: 12px; padding: 4px 10px; }
                    .fc .fc-daygrid-event {
                        border-radius: 4px;
                        font-size: 11px;
                        font-weight: bold;
                        padding: 1px 4px;
                        cursor: pointer;
                    }
                    .fc .fc-event:hover { opacity: 0.85; }
                    .fc .fc-col-header-cell { background: #f5f5f5; }
                    .fc .fc-day-today { background: #e6f4ff !important; }
                `}</style>

                <FullCalendar
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    locale="ko"
                    events={calendarEvents}
                    height={580}
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: '',
                    }}
                    buttonText={{ today: '오늘' }}
                    datesSet={loadCalendarData}
                    eventDisplay="block"
                    dayMaxEvents={4}
                    moreLinkText={(n) => `+${n}명 더보기`}
                />
            </Spin>
        </Modal>
    );
};

export default VacationCalendarModal;